import React, { useEffect, useState } from "react";
import { Button, Form, List, message, Modal, Select } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { FIRESTORE_CUSTOMERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import dayjs from "dayjs";

const { Option } = Select;

const EditPriceListCustomersModal = ({ visible, onClose, priceList, customers }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { email } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  // Get customers currently assigned to this price list
  const assignedCustomers = customers.filter((customer) => customer.priceListId === priceList?.id);

  useEffect(() => {
    if (visible && priceList) {
      const customerIds = assignedCustomers.map((c) => c.id);
      setSelectedCustomers(customerIds);
      form.setFieldsValue({
        customerIds: customerIds,
      });
    }
  }, [visible, priceList, customers]);

  const handleAddCustomer = (customerId) => {
    if (!selectedCustomers.includes(customerId)) {
      const newSelectedCustomers = [...selectedCustomers, customerId];
      setSelectedCustomers(newSelectedCustomers);
      form.setFieldsValue({
        customerIds: newSelectedCustomers,
      });
    }
  };

  const handleRemoveCustomer = (customerId) => {
    const newSelectedCustomers = selectedCustomers.filter((id) => id !== customerId);
    setSelectedCustomers(newSelectedCustomers);
    form.setFieldsValue({
      customerIds: newSelectedCustomers,
    });
  };

  // Filter available customers (not yet selected)
  const availableCustomers = customers
    .filter((customer) => customer.status === "active" && !selectedCustomers.includes(customer.id))
    .sort((a, b) => a.customerName?.localeCompare(b.customerName));

  // Get selected customer objects for display
  const selectedCustomerObjects = selectedCustomers
    .map((id) => customers.find((c) => c.id === id))
    .filter(Boolean)
    .sort((a, b) => a.customerName?.localeCompare(b.customerName));

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const newCustomerIds = values.customerIds || [];
      const currentCustomerIds = assignedCustomers.map((c) => c.id);

      // Find customers to add (in new list but not in current)
      const toAdd = newCustomerIds.filter((id) => !currentCustomerIds.includes(id));

      // Find customers to remove (in current but not in new list)
      const toRemove = currentCustomerIds.filter((id) => !newCustomerIds.includes(id));

      // Update customers that need to be added
      for (const customerId of toAdd) {
        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, customerId), {
          priceListId: priceList.id,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        });
      }

      // Update customers that need to be removed
      for (const customerId of toRemove) {
        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, customerId), {
          priceListId: null,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        });
      }

      message.success(t("notifications.price_list_customers_updated"));
      onClose();
    } catch (error) {
      console.error("Error updating price list customers:", error);
      message.error(t("notifications.error_updating_price_list_customers"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("edit_price_list_customers", { name: priceList?.name })}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("cancel")}
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {t("save")}
        </Button>,
      ]}
      width={600}
      centered
      zIndex={1100}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="customerIds" hidden>
          <Select mode="multiple" />
        </Form.Item>

        <Form.Item label={t("add_customer")}>
          <Select
            placeholder={t("form.select_customer")}
            showSearch
            value={null}
            optionFilterProp="children"
            filterOption={(input, option) => option.children?.toLowerCase().includes(input.toLowerCase())}
            onChange={handleAddCustomer}
          >
            {availableCustomers.map((customer) => (
              <Option key={customer.id} value={customer.id}>
                {customer.customerName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label={t("assigned_customers")}>
          {selectedCustomerObjects.length === 0 ? (
            <div style={{ padding: "8px 0", color: "#999" }}>{t("no_customers_assigned")}</div>
          ) : (
            <List
              bordered
              dataSource={selectedCustomerObjects}
              renderItem={(customer) => (
                <List.Item
                  actions={[
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveCustomer(customer.id)}
                    />,
                  ]}
                >
                  {customer.customerName}
                </List.Item>
              )}
            />
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditPriceListCustomersModal;
